import React from 'react';
import { ClipboardIcon, CalendarIcon, ClockIcon, CheckIcon, CircleIcon, SaveIcon } from '@bizfirst/common-react';
import { ExecutionObserverConfig, SavedObserverConfig } from '../../models/ExecutionObserverConfig';
import '../../styles/tabs/SavedConfigsTab.css';

interface SavedConfigsTabProps {
  savedConfigs: SavedObserverConfig[];
  onLoad: (config: SavedObserverConfig) => void;
  onDelete: (id: string) => void;
  readonly?: boolean;
}

export const SavedConfigsTab: React.FC<SavedConfigsTabProps> = ({
  savedConfigs,
  onLoad,
  onDelete,
  readonly = false
}) => {
  if (savedConfigs.length === 0) {
    return (
      <fieldset className="saved-configs-tab" disabled={readonly}>
        <legend>Saved Configurations</legend>
        <div className="empty-state">
          <p><ClipboardIcon size={14} /> No saved configurations yet.</p>
          <p>Create one by clicking "Save As..." in the modal footer.</p>
        </div>
      </fieldset>
    );
  }

  return (
    <fieldset className="saved-configs-tab" disabled={readonly}>
      <legend>Saved Configurations</legend>

      <div className="config-list">
        {savedConfigs.map((cfg) => (
          <div key={cfg.id} className="config-item">
            <div className="config-header">
              <h4>{cfg.name}</h4>
              {cfg.description && <small className="config-desc">{cfg.description}</small>}
            </div>

            <div className="config-meta">
              <span className="meta-item">
                <CalendarIcon size={12} /> Created: {new Date(cfg.createdAt).toLocaleDateString()}
              </span>
              {cfg.lastUsedAt && (
                <span className="meta-item">
                  <ClockIcon size={12} /> Last used: {new Date(cfg.lastUsedAt).toLocaleDateString()}
                </span>
              )}
              <span className="meta-item">
                <CheckIcon size={12} /> Used {cfg.useCount} {cfg.useCount === 1 ? 'time' : 'times'}
              </span>
            </div>

            <div className="config-preview">
              <small>
                {cfg.config.enabled ? <><CheckIcon size={12} /> Enabled</> : <><CircleIcon size={12} /> Disabled</>} •{' '}
                {cfg.config.detailLevel || 'detailed'} detail
              </small>
            </div>

            <div className="config-actions">
              <button
                className="btn btn-primary"
                onClick={() => onLoad(cfg)}
                disabled={readonly}
              >
                Load
              </button>
              <button
                className="btn btn-danger"
                onClick={() => {
                  if (window.confirm(`Delete "${cfg.name}"?`)) {
                    onDelete(cfg.id);
                  }
                }}
                disabled={readonly}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="info-box">
        <p>
          <SaveIcon size={14} /> <strong>Saved Configurations:</strong> Use these to quickly switch between
          different observer settings for testing, debugging, and production monitoring.
        </p>
      </div>
    </fieldset>
  );
};

export default SavedConfigsTab;
